import { useLayoutEffect, useRef } from "react";
import { computeDropIndex, DragAxis } from "components/dragReorder";

export interface DragReorderOptions<S> {
    axis: DragAxis
    /** Element that follows the pointer while dragging. */
    ghostRef: { readonly current: HTMLElement | null | undefined }
    /** Offset between the pointer and the ghost's top-left corner. */
    pivot: (state: S) => { x: number, y: number }
    /** Index the dragged item currently occupies in the rendered list. */
    index: (state: S) => number
    /** When given, the ghost is also sized on every move. */
    ghostSize?: (state: S) => { w: number, h: number }
    /** Bounding boxes of the rendered items, or undefined when they can't be measured. */
    getBoxes: () => DOMRect[] | undefined
    onIndexChange: (state: S, next: number) => void
    onDrop: (state: S) => void
}

// NS-731: shared document mousemove/mouseup lifecycle for drag-reorder, used
// by DataTable and ReorderableCheckList. Listeners are (re)attached whenever
// the drag state changes, as the previous per-component effects did; the
// options are read through a ref so callbacks always see the latest render.
export function useDragReorder<S>(active: S | undefined, options: DragReorderOptions<S>) {
    const optionsRef = useRef(options);
    useLayoutEffect(() => {
        optionsRef.current = options;
    });

    useLayoutEffect(() => {
        if (active === undefined) {
            return;
        }

        const onMouseMove = (e: MouseEvent) => {
            const opts = optionsRef.current;
            const ghost = opts.ghostRef.current;
            if (ghost) {
                const pivot = opts.pivot(active);
                ghost.style.left = `${e.clientX - pivot.x}px`;
                ghost.style.top = `${e.clientY - pivot.y}px`;
                if (opts.ghostSize) {
                    const size = opts.ghostSize(active);
                    ghost.style.width = `${size.w}px`;
                    ghost.style.height = `${size.h}px`;
                }
            }

            const boxes = opts.getBoxes();
            if (!boxes || !boxes.length) {
                return;
            }

            const current = opts.index(active);
            const next = computeDropIndex(boxes, current, opts.axis === "x" ? e.clientX : e.clientY, opts.axis);
            if (next !== current) {
                opts.onIndexChange(active, next);
            }
        };
        const onMouseUp = () => {
            optionsRef.current.onDrop(active);
        };

        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
        return () => {
            document.removeEventListener("mouseup", onMouseUp);
            document.removeEventListener("mousemove", onMouseMove);
        };
    }, [active]);
}
